/**
 * The surface registry — which surfaces exist, and which of them can go stale.
 *
 * M0-R9 and spec §0.5 both require this and neither had it: "the surface
 * registry records which kind each surface is; the coverage test asserts five
 * for bank-backed surfaces and four otherwise". Until now `registry.tsx` had no
 * kind field and `StatesReference.test.tsx` asserted *five states for every
 * primitive*, which is the opposite of the distinction D20 turns on. Issue 251.
 *
 * **Why a surface has a kind at all.** Stale is bank-sync freshness reported by
 * the server — `last_synced_at` six days old while the request itself succeeded.
 * Web is online-only (offline is a non-goal, master PRD §3), so there is no
 * client cache to age. A surface with no upstream sync therefore has no way to
 * BE stale, and D20 named four of them outright: categories, budgets, rules,
 * reports. Budgets have since become `synced` (M4 decision E5, see the
 * `budgets` row); the other three stay live. Those implement four states, not five, and a stale branch on them is
 * unreachable code pretending to be coverage.
 *
 * **Registered when a surface exists, not before.** The twelve-module treatment
 * matrix in spec §0.5.1 names every module the design covers, and it is
 * tempting to seed all twelve here. That is the failure #229 records — a
 * primitive built with no caller — one level up: a registry row with no surface
 * is a kind nobody has had to defend against real data. Each module registers
 * its own row when it lands, and §0.5.1 is where the design's intent waits in
 * the meantime.
 *
 * So this list is SHORT on purpose. Three real surfaces exist today.
 */

/**
 * `synced` — data whose freshness comes from an upstream bank sync, so the
 * server can hand back true-but-old data and the surface must say so. Five
 * states.
 *
 * `live` — data that is only ever as old as the request that fetched it.
 * Four states; `stale` is not available and is a defect if it appears.
 */
export type SurfaceKind = 'synced' | 'live'

/**
 * Every surface that routes through `<Async>`, with the kind that decides how
 * many states it has.
 *
 * Adding a row is the point of friction: it forces the question "can this go
 * stale?" to be answered once, in writing, rather than implied by whether
 * someone remembered to pass `asOf`.
 */
export const SURFACES = {
  /**
   * The notifications drawer. `live` — notifications are generated by our own
   * backend from events, never fetched from a bank, so there is no
   * `last_synced_at` to be behind. `useNotifications` passes no `asOf` and
   * `NotificationsDrawer.test.tsx:193` asserts stale is unreachable there;
   * this is that assertion's reason, stated once instead of per test.
   */
  notifications: 'live',

  /**
   * `/states`, the primitive catalogue. `synced` because the page's whole job
   * is to render all five states from fixtures — it is a specimen sheet, not a
   * surface with data, and the one place a stale specimen SHOULD appear.
   *
   * The honest caveat: calling it `synced` makes the invariant below vacuous
   * for this route. That is deliberate and better than an `exempt` third kind,
   * which would be a hole every future surface could be pushed through.
   */
  'states-reference': 'synced',

  /**
   * `ErrorBoundary`'s internal use — it routes a caught render throw through
   * Async's error branch so a crash looks like every other failure. `live`: a
   * component that threw has no data at all, let alone dated data.
   */
  'error-boundary': 'live',

  /**
   * The categories list. `live` — D20 names categories outright as a surface
   * with no upstream sync, so there is no `last_synced_at` to be behind. Four
   * states, and `useCategories` passes no `asOf`.
   *
   * There is no empty state either, and that is data rather than a choice: 88
   * system categories are seeded with `user_id = NULL` and returned to every
   * user from first login, so `isEmpty` would be dead code. See epic #12.
   */
  categories: 'live',

  /**
   * The merchants list. `live` — `MerchantResponse` carries no
   * `last_synced_at`. Global merchants originate at Plaid, which is the one
   * thing here that could plausibly be behind, but nothing on the wire reports
   * that origin's freshness, so there is no date to put in a stale banner and
   * the branch would be unreachable code pretending to be coverage.
   *
   * Unlike `categories`, the EMPTY state is reachable: a user who has created
   * no merchants and filters global ones out has genuinely none. See M11.
   */
  merchants: 'live',

  /**
   * `/tags`. `live` — `TagList` is a distinct-values read with no `last_synced_at`; empty is
   * reachable (a user who never tagged).
   */
  tags: 'live',

  /**
   * The transactions list embedded in `/merchants/:id` — Task 11.
   *
   * `live` for the same reason `merchants` is: `TransactionResponse` carries
   * no `last_synced_at` either, so there is no date a stale banner on this
   * list could report. A separate row from `merchants` rather than reusing
   * it, because the two calls on that screen fail independently — a merchant
   * that loaded fine can still have its transactions request fail — and one
   * `<Async>` cannot speak for two different queries at once.
   */
  'merchant-transactions': 'live',

  /**
   * The household member list. `live` — `MembershipResponse` carries no
   * `last_synced_at` and household membership has no upstream sync to be
   * behind. D20.
   *
   * The EMPTY state is unreachable and is not built: `list_members` returns
   * every ACTIVE membership, and the signed-in user reading this screen is by
   * definition one of them. The spec says so outright — "Members: never empty".
   */
  'household-members': 'live',

  /**
   * Pending invitations. A separate row from `household-members` rather than
   * reusing it, for the reason `merchant-transactions` is separate from
   * `merchants`: the two calls on this screen fail independently, and one
   * `<Async>` cannot speak for two queries.
   *
   * Unlike members, EMPTY is the common case and is built — a household with
   * nobody invited is the normal state.
   */
  'household-invitations': 'live',

  /**
   * `/review`, M6. `live` (Q7): the review inbox is our own backend's state, so there is no
   * sync it can be behind. `refreshed_at` is shown as copy ("fetched in the 3:47 pm refresh"),
   * never as a stale banner.
   */
  review: 'live',

  /**
   * The accounts list, M5. `synced` — the first row in this registry that is.
   * `AccountResponse.last_synced_at` is bank-sync freshness reported by the
   * server, which is exactly D20's definition of stale: the request can
   * succeed and still hand back true-but-old data. A manual account has
   * nothing to sync and reports no `last_synced_at`; `useAccounts` treats a
   * list of entirely-manual accounts as current rather than stale.
   */
  accounts: 'synced',

  /**
   * `/connections`, M5 Ticket 3. `synced` for the identical reason `accounts`
   * is: `ProviderItemResponse.last_synced_at` is bank-sync freshness reported
   * by the server, independent of any one account under that item.
   */
  'aggregator-items': 'synced',

  /**
   * The reconnect match step (`GET /aggregator/items/{id}/relink`). `live`:
   * `RelinkState` carries no sync date, so there is nothing for it to be stale
   * against — it is the server's CURRENT pairing, refetched while `pending`.
   * Rendering `stale` on it throws.
   */
  'relink-state': 'live',

  /**
   * `Connect.tsx`'s provider picker, M5 Ticket 3. `live` — the catalogue is
   * `ProviderResolver.resolve()`'s CURRENT registry state, not bank-sync
   * freshness; `ProviderCatalogResponse` carries no `last_synced_at` at all,
   * so a stale banner here would have no date to report.
   */
  'aggregator-providers': 'live',

  /**
   * The detail hero's "In {month}" figure, M5 Ticket 4 — `useAccountSummary
   * .ts`'s own nested surface, the `OwnerMenu`/Connections-tile precedent
   * (rule 10: a second call on a page is a second surface, not folded into
   * the page's own `surface="accounts"`).
   *
   * `live`, for the identical reason `merchant-transactions` is: `Transaction
   * Summary` (`total_income`/`total_expenses`/`net`/`transaction_count`/
   * `by_category`) carries no `last_synced_at` of its own — it is a computed
   * aggregate over a date range, not a bank-synced record — so there is no
   * date a stale banner on THIS surface could report. `accounts` itself stays
   * `synced`; this is a different resource with different freshness facts.
   */
  'account-summary': 'live',

  /**
   * The detail screen's "This cycle" card (M5, backend 551). `StatementCyclesResponse`
   * carries no `last_synced_at`, so there is no date a stale banner on THIS
   * surface could report: it is an aggregate over synced transactions, and the
   * page's own `accounts` surface (`synced`) already shows "Showing data from …"
   * for the same account. A second banner would repeat it — the `account-summary`
   * precedent.
   */
  'statement-cycles': 'live',

  /**
   * The detail screen's "Transactions on this account" panel — pulled
   * forward from M6 by explicit user instruction (the 2026-09-17 design
   * gap-fill pass on Ticket 4), reading `GET /transactions?account_id=`.
   * `live` for the identical reason `merchant-transactions` is:
   * `TransactionResponse` carries no `last_synced_at`.
   */
  'account-transactions': 'live',

  /**
   * The category form's icon picker, #447 — the group tabs and the icon grid
   * over `/category-icons`. `live`: the catalogue is backend-owned reference
   * data that changes only when a migration ships one, and neither
   * `CategoryIconList` nor `CategoryIconGroupList` carries a
   * `last_synced_at`. The EMPTY state is reachable — a search that matches
   * nothing.
   */
  'category-icons': 'live',

  /**
   * Settings › Preferences, M13: the current user's own record. `live` — `UserResponse` carries
   * no `last_synced_at`, so there is nothing for it to be stale against.
   */
  preferences: 'live',

  /**
   * Settings › Preferences › Notifications, M13: the user's own `notification-preferences` record. `live` —
   * it carries no `last_synced_at`, so there is nothing for it to be stale against.
   */
  'notification-preferences': 'live',

  /**
   * Settings › Profile, M13: the same `UserResponse` record as `preferences`, so `live` for the
   * same reason — no `last_synced_at` on it.
   */
  profile: 'live',

  /**
   * The rules list, M11. `live` — D20 names rules among the four surfaces with
   * no upstream sync, and `RuleResponse` carries no `last_synced_at`, so there
   * is nothing for it to be stale against. Empty (no rules yet) is reachable.
   */
  rules: 'live',

  /**
   * Suggested rules, M11. `live` for the same reason as `rules`: the
   * suggestions are derived by our own backend and carry no `last_synced_at`.
   * Read by the Suggestions tab's `<Async surface="ruleSuggestions">`
   * (routes/rules/RuleSuggestions.tsx).
   */
  ruleSuggestions: 'live',

  /**
   * A rule's "Applied to" tab, M11 (rule view): `GET /rule-logs?rule_id=`. `live`: a log row is
   * written by our own rule engine and carries `applied_at`, which is when the rule ran, not a bank
   * sync date, so there is nothing for it to be stale against. Its own surface so a failure here
   * leaves the rule page itself standing. Empty (the rule has changed nothing) is reachable.
   */
  'rule-applied': 'live',

  /**
   * A rule's "Matches now" tab, M11 (rule view): `GET /rules/{id}/matches`, a dry run over the
   * user's own transactions computed per request. `live`: nothing in it comes from a bank sync, and
   * the response carries no sync date. Empty (the rule matches nothing) is reachable.
   */
  'rule-matches': 'live',

  /**
   * A rule's "Removed" tab, M11 (rule view): `GET /rules/{id}/exclusions`. `live`: exclusions are
   * the user's own choices, with no sync behind them. Empty (nothing removed) is reachable.
   */
  'rule-removed': 'live',

  /**
   * The M6 transactions list. `live` (Q7) — `TransactionResponse` carries no
   * `last_synced_at`, so there is no date a stale banner could report, and
   * rendering `stale` here throws. The screen's own freshness is the request.
   */
  transactions: 'live',

  /**
   * `/transactions/:id` — M6 Task 11. `live` for the list's reason: the row
   * carries no `last_synced_at`, so there is no date a stale note could report.
   */
  'transaction-detail': 'live',

  /**
   * The merchant stats panel on `/transactions/:id` — M6 Task 14. `live`: it is
   * folded from the same `/transactions` rows, which carry no sync date. Its own
   * surface so a failure here leaves the rest of the detail standing.
   */
  'merchant-stats': 'live',

  /**
   * `/recurring`, M7 (P1): the month's grid and list on one screen (#704; the
   * old `recurring-calendar` surface went with the separate calendar view).
   * `live` — no recurring response carries a sync date,
   * so `stale` has no date to show and rendering it throws. Bank freshness
   * reaches the user as the reconnect flag and banner (`account_status`), not
   * as a stale state. M6 transactions made the same call.
   */
  recurring: 'live',

  /**
   * M7's Choose-payment sheet (Task 6): one account's transactions within ±15
   * days of a bill's due date. `live` for the reason `merchant-transactions`
   * is — `TransactionResponse` carries no sync date — and its own entry because
   * it fails independently of the list behind it, with its own sentence.
   */
  'recurring-payment-picker': 'live',

  /**
   * `/recurring/all`, M7 Task 15. `live` for the reason `recurring` is — a
   * stream list with no sync date. Its own entry: its own screen and skeleton.
   */
  'recurring-all': 'live',

  /**
   * `/recurring/:id`, M7 Task 9 (P1). `live` for the reason `recurring` is —
   * neither the bill nor its payments timeline carries a sync date. Bank
   * freshness is S11's reconnect note on the screen, never a stale state. A
   * removed bill (`is_removed` or a 404) is the screen's own state, not one of
   * these four.
   */
  'recurring-detail': 'live',

  /**
   * `/recurring/suggestions`, M7 Task 14. `live` for the reason `recurring`
   * is — a suggestion carries `detected_at`, which is when the server looked,
   * not a bank sync date. "Nothing new detected" is the empty state and reads
   * as success (R20), never as a stale or failed one.
   */
  'recurring-suggestions': 'live',

  /**
   * `/cashflow`, M3 (owner answer A23: a live surface, four states, no stale).
   * The response is a computed aggregate over a date window — totals, buckets,
   * categories — and carries no `last_synced_at`, so there is no date a stale
   * note could report and rendering `stale` here throws. Bank freshness is not
   * this screen's to show; M6 transactions and M7 recurring made the same call.
   */
  cashflow: 'live',
  /** Reports (M8): computed on request from the cashflow pipeline, no sync date; D20 named reports live. */
  reports: 'live',

  /**
   * `/budget`, M4. `synced` by owner decision E5 (issue 5): budgets carry no
   * `last_synced_at` of their own, but every `spent` is summed from bank-synced
   * transactions, so the page is stale when any account is, dated from the oldest
   * `last_synced_at` — `useAccounts()`'s own `asOf`.
   */
  budgets: 'synced',

  /**
   * M2 Overview's net-worth hero. `synced` — it shows the accounts' balances,
   * each of which carries `last_synced_at`, so it can be true-but-old and says
   * so. Stale is dated from the oldest counted sync, M5's own rule.
   */
  'overview-net-worth': 'synced',

  /**
   * M2 Overview's remaining widgets are `live`, the same call M3, M6 and M7
   * made: each reads a computed aggregate or our own records, none of which
   * carries a sync date, so there is no date a stale note could report.
   * Budget envelopes use `budgets` above, because M4 made that surface synced.
   */
  'overview-upcoming': 'live',
  'overview-spent': 'live',
  'overview-where-it-went': 'live',
  'overview-recent': 'live',
  'overview-cashflow': 'live',
  /**
   * M2 Overview's Insight card, on `GET /insights`. `live`: the response carries
   * `generated_at` (the request time, "computed on request") and no
   * `last_synced_at`, so there is no bank-sync date a stale note could report.
   * It is the same kind of thing as Spent and Where it went beside it, which
   * show the same category money as live; stale here would give one page two
   * freshness answers for one figure.
   */
  'overview-insight': 'live',
  /**
   * M13 Settings › Devices, on `GET /devices`. `live`: a list of this account's
   * own devices with `last_seen_at`, and no bank-sync date a stale note could report.
   */
  devices: 'live',
  /**
   * M13 Settings › Security, the backend half: `GET /mfa/status` and `GET /mfa/backup-codes`.
   * `live`: a count and a flag about the account's own factors, no bank sync behind them.
   */
  'security-mfa': 'live',
} as const satisfies Record<string, SurfaceKind>

export type Surface = keyof typeof SURFACES

/** Whether `surface` may render the `stale` state. */
export function canGoStale(surface: Surface): boolean {
  return SURFACES[surface] === 'synced'
}
